import { apiRequest } from '@/lib/api-client';

import type {
  AccountClaimedAnswer,
  AccountSetUpInspection,
  CompleteAccountSetUpInput,
  CompletedAccountSetUp,
  ReplacementLinkNotice,
} from './types';

/**
 * The claim slice's only HTTP surface (`USR-013`).
 *
 * **Bare functions rather than TanStack hooks**, which is `apps/organizer`'s
 * `accept-invitation-page.tsx` shape and is the right one here for the same reason: the page runs
 * one inspection on mount from a `useEffect` with an `AbortController`, and a query cache keyed on
 * a **credential** is a credential in a cache. There is nothing to invalidate afterwards either —
 * a successful set-up navigates to sign-in, and this app holds no state about an account it does
 * not yet have a session for.
 *
 * All three are declared in the API's `EDGE_PUBLIC_ROUTES`. Without that declaration API Gateway
 * answers 401 to a person opening a link out of their inbox, which is a failure invisible to every
 * test in this repository and has happened three times.
 */

export async function inspectAccountSetUpLink(
  token: string,
  signal?: AbortSignal,
): Promise<AccountSetUpInspection> {
  return await apiRequest<AccountSetUpInspection>('/api/users/account-set-up/inspect', {
    method: 'POST',
    body: { token },
    ...(signal === undefined ? {} : { signal }),
  });
}

export async function completeAccountSetUp(
  input: CompleteAccountSetUpInput,
): Promise<CompletedAccountSetUp> {
  return await apiRequest<CompletedAccountSetUp>('/api/users/account-set-up/complete', {
    method: 'POST',
    body: {
      token: input.token,
      password: input.password,
      ...(input.displayName === undefined ? {} : { displayName: input.displayName }),
    },
  });
}

/**
 * Asks for a fresh link, presenting the expired one (`USR-013` AC-7).
 *
 * **It sends the token and never an address**, and that is the whole of the design. The server
 * answers the same sentence whatever it found, and mails only to the address on the row the token
 * names — so somebody holding a stranger's expired link causes one mail to the stranger and
 * learns nothing.
 */
export async function requestAnotherAccountSetUpLink(
  token: string,
): Promise<ReplacementLinkNotice> {
  return await apiRequest<ReplacementLinkNotice>('/api/users/account-set-up/replacement', {
    method: 'POST',
    body: { token },
  });
}

/**
 * **What became mine that nobody has told me about** (`USR-009` AC-3).
 *
 * A session-bearing read, unlike the three above — by the time a claim resolves there is an
 * account, which is the difference between this criterion and the story that built the link.
 *
 * It is deliberately **not** a TanStack query. The answer it returns is consumed by
 * {@link acknowledgeAccountClaimed}, so a cache that replayed it would re-show a confirmation the
 * member has already dismissed — the exact failure that keeps this off the `/mine` payload.
 */
export async function fetchAccountClaimed(signal?: AbortSignal): Promise<AccountClaimedAnswer> {
  return await apiRequest<AccountClaimedAnswer>('/api/event-registrations/mine/account-claim', {
    method: 'GET',
    ...(signal === undefined ? {} : { signal }),
  });
}

/** The member has read it (`USR-009` AC-3). Idempotent on the server; safe to lose. */
export async function acknowledgeAccountClaimed(): Promise<{ readonly confirmed: number }> {
  return await apiRequest<{ readonly confirmed: number }>(
    '/api/event-registrations/mine/account-claim',
    { method: 'POST', body: {} },
  );
}
